import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Percent,
  Trash2,
  Calendar,
  User,
  Activity,
  Loader2,
  Database,
  ShieldCheck,
  CheckCircle2,
  Scale
} from 'lucide-react';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import styles from '../../../styles/MasterView.module.css';
import api from '../../../api/axios';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import TaxDrawer from './TaxDrawer';
import NotFound from '../../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewTax = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tax, setTax] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchTax = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/taxes/${id}`);
      if (response.data.success) {
        setTax(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tax details:', error);
      toast.error('Failed to load tax details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTax();
  }, [id]);

  const handleDelete = () => {
    MySwal.fire({
      title: 'Delete Tax?',
      text: `Are you sure you want to delete ${tax.tax_name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/taxes/${tax.id}`);
          if (response.data.success) {
            toast.success('Tax deleted successfully');
            navigate('/taxes');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting tax');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className={styles['view-master-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Tax Configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tax) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-master-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Tax Configuration</h1>
              <p>View tax percentages and financial reporting classification</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/taxes')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Update Tax</span>
              </button>
              <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444' }}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Master Profile Header Card */}
          <div className={styles['master-profile-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ background: 'var(--primary-gradient)' }}
              >
                <Percent size={32} />
              </div>
              <div className={styles['profile-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{tax.tax_name}</h1>
                  <span className={styles['status-badge']}>
                    <ShieldCheck size={14} /> ACTIVE TAX
                  </span>
                </div>
                <div className={styles['master-meta']}>
                  <div className={styles['meta-item']}>
                    <Scale size={16} />
                    <span>Rate: <strong>{tax.tax_percentage}%</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Configured: <strong>{new Date(tax.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Database size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Linked POs</label>
                <h2>85</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><CheckCircle2 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Linked Invoices</label>
                <h2>142</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><Activity size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Revision History</label>
                <h2>02</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><ShieldCheck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Compliance</label>
                <h2>GSTD</h2>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Primary Details */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Scale size={20} /></div>
                  <h3>Tax Identification</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Official Label</span>
                      <span className={styles['d-value']}>{tax.tax_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Applicable Percentage</span>
                      <span className={styles['d-value']}>{tax.tax_percentage}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Revision History */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Revision Index</h3>
                </div>
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                  <p style={{ fontSize: '13px' }}>Audit log for tax rate changes will be available in the next financial module sync.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Registry Info */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['info-block']}>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><User size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>CONFIGURED BY</label>
                       <span>{tax.creator?.name || 'System Admin'}</span>
                    </div>
                  </div>
                  <div className={styles['meta-sidebar-item']}>
                    <div className={styles['sidebar-icon']}><Calendar size={18} /></div>
                    <div className={styles['sidebar-text']}>
                       <label>CREATED ON</label>
                       <span>{new Date(tax.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <TaxDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tax={tax}
        onSave={fetchTax}
      />
    </div>
  );
};

export default ViewTax;
